import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { httpSignUpUser } from '../Hooks/user/user.post.hooks';
import { Link, useNavigate } from 'react-router-dom';
import Auth from '../Helpers/auth';
import AuthImage from '../Visuals/Images/Authentication.jpg';
import {
  AuthContainer,
  PictureSide,
  AuthenticationSide,
  AuthContainer2,
  AuthHeader,
  NameFieldsRow,
  LegalText,
} from './AuthStyles.styles';
import HelmetHelper from '../Components/PageHelmet/HelmetHelper.component';

const PrimaryButton = React.lazy(
  () => import('../Components/Buttons/PrimaryButton/PrimaryButton.component')
);
const InputContainer = React.lazy(() => import('../Components/Form/Input'));
const PasswordInput = React.lazy(
  () => import('../Components/Form/PasswordInput')
);
const SignupOptions = React.lazy(() => import('./SignupOptions.component'));

const SignUpComponent = () => {
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);
  const [verifyShowPass, setVerifyShowPass] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verifyPassword: '',
  });

  // Mutation for signing up the user
  const mutation = useMutation({
    mutationFn: (newUser) => httpSignUpUser(newUser),
    onSuccess: async (data) => {
      try {
        const dataJ = await data.json();

        // If the response is not OK, show the server-provided error message
        if (!data.ok) {
          const serverMessage = dataJ?.message || dataJ?.error || null;
          setError(serverMessage || 'Your account could not be created.');
          return;
        }

        // Save the token and set success message
        Auth.login(dataJ.token);
        localStorage.setItem('temp_id', dataJ.user[0]._id);
        setSuccess(
          'Your account has been created successfully! Redirecting...'
        );

        // Delay navigation to allow the user to see the success message
        setTimeout(() => {
          navigate('/user');
        }, 2000);
      } catch (err) {
        console.error('Error parsing signup response:', err);
        setError(
          err?.message || 'An unexpected error occurred. Please try again.'
        );
      }
    },
    onError: (error) => {
      console.error('Error signing up:', error);
      // Try common error shapes
      const serverMsg =
        error?.response?.data?.message ||
        error?.message ||
        (error && typeof error === 'string' ? error : null);

      setError(serverMsg || 'Your account could not be created.');
    },
  });

  // Handle form input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState({
      ...formState,
      [name]: value,
    });
    if (error) {
      setError('');
    }
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();

    const trimmedFormState = {
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim(),
      password: formState.password,
      verifyPassword: formState.verifyPassword,
      isIP: false,
    };

    // Basic validation
    if (trimmedFormState.firstName.length < 2) {
      setError('First Name must be at least 2 characters long.');
      return;
    }
    if (trimmedFormState.lastName.length < 3) {
      setError('Last Name must be at least 3 characters long.');
      return;
    }
    if (
      trimmedFormState.email.length < 6 ||
      !trimmedFormState.email.includes('@')
    ) {
      setError('Please enter a valid email address.');
      return;
    }
    if (trimmedFormState.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (trimmedFormState.password !== trimmedFormState.verifyPassword) {
      setError('Passwords do not match.');
      return;
    }

    mutation.mutate(trimmedFormState);
  };

  return (
    <AuthContainer>
      <HelmetHelper
        title="Sign Up"
        description="Create an account to purchase courses, access online training, track your progress, and manage your certificates."
        noIndex
      />
      <AuthContainer2>
        <PictureSide>
          <img src={AuthImage} loading="eager" alt="Yellow helmets" />
        </PictureSide>

        <AuthenticationSide>
          <form onSubmit={handleFormSubmit}>
            <AuthHeader>
              <p className="eyebrowHeader">Create Account</p>

              <h2>Sign Up</h2>

              <p>
                Create your account to access training, certificates, course
                progress, and account details.
              </p>
            </AuthHeader>

            <NameFieldsRow>
              <InputContainer
                label="First Name"
                value={formState.firstName}
                name="firstName"
                onChange={handleChange}
              />

              <InputContainer
                label="Last Name"
                value={formState.lastName}
                name="lastName"
                onChange={handleChange}
              />
            </NameFieldsRow>

            <InputContainer
              label="Email"
              value={formState.email}
              name="email"
              onChange={handleChange}
            />

            <PasswordInput
              label="Password"
              span="Must be at least 6 characters"
              handleChange={(e) => {
                e.preventDefault();
                setShowPass((prev) => !prev);
              }}
              showPass={showPass}
              value={formState.password}
              name="password"
              onChange={handleChange}
            />

            <PasswordInput
              label="Verify Password"
              handleChange={(e) => {
                e.preventDefault();
                setVerifyShowPass((prev) => !prev);
              }}
              value={formState.verifyPassword}
              onChange={handleChange}
              name="verifyPassword"
              showPass={verifyShowPass}
            />

            {success && <p className="success">{success}</p>}
            {error && <p className="error">{error}</p>}

            <PrimaryButton span="Sign Up" />

            <LegalText>
              By signing up, you agree to our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
              <Link to="/terms-and-conditions">Terms & Conditions</Link>.
            </LegalText>
          </form>

          <SignupOptions />
        </AuthenticationSide>
      </AuthContainer2>
    </AuthContainer>
  );
};

export default SignUpComponent;
